"use client";
import { LinkProps } from "next/link";
import React from "react";
import { Link } from "@/i18n/routing";

interface TransitionLinkProps extends LinkProps {
  children: React.ReactNode;
  href: string;
  classes?: string;
  onPointerUp?: (e: React.PointerEvent<HTMLAnchorElement>) => void;
  pointerUpDelay?: number;
  title?: string;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getLinkHref(linkElement: HTMLAnchorElement) {
  const linkUrl = new URL(linkElement.href);

  if (linkUrl.origin !== window.location.origin) {
    return linkElement.href;
  }

  return `${linkUrl.pathname}${linkUrl.search}${linkUrl.hash}`;
}

function normalizeHref(href: string) {
  const url = new URL(href, window.location.origin);

  if (url.origin !== window.location.origin) {
    return url.href;
  }

  const pathname = url.pathname.replace(/\/+$/, "") || "/";

  return `${pathname}${url.search}${url.hash}`;
}

const TransitionLink: React.FC<TransitionLinkProps> = ({
  children,
  href,
  classes,
  onPointerUp,
  title,
  pointerUpDelay = 500, // Default delay of 500ms
}) => {
  const handleTransition = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    e.preventDefault();
    const linkElement = e.currentTarget;
    const targetHref = getLinkHref(linkElement);
    const currentHref = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (normalizeHref(targetHref) === normalizeHref(currentHref)) {
      return;
    }

    document.body.classList.add("page-transition");

    await sleep(500);
    window.location.href = targetHref;
  };

  const handlePointerUp = async (e: React.PointerEvent<HTMLAnchorElement>) => {
    if (onPointerUp) {
      await sleep(pointerUpDelay); // Add delay before calling onPointerUp
      onPointerUp(e);
    }
  };

  return (
    <Link
      className={classes}
      href={href}
      onClick={handleTransition}
      onPointerUp={handlePointerUp}
      title={title ?? undefined}
    >
      {children}
    </Link>
  );
};

export default TransitionLink;
